/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { ComponentProps, CSSProperties, ReactElement } from 'react';
import { cssMap } from '@compiled/react';

import { jsx } from '@atlaskit/css';
import type { Media as OriginalMedia } from '@atlaskit/renderer/nodes';
import { token } from '@atlaskit/tokens';

import type { LightNodeProps } from '../LightNodeObserver.ts';

const styles = cssMap({
	media: {
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.small'),
		maxHeight: '100%',
		maxWidth: '100%',
	},
});

const getMediaDimensions = (
	props: ComponentProps<typeof OriginalMedia>,
): CSSProperties | undefined => {
	const width = props.cardDimensions?.width ?? props.width;
	const height = props.cardDimensions?.height ?? props.height;

	if (width === undefined && height === undefined) {
		return undefined;
	}

	return { height, width };
};

export const Media = (
	props: ComponentProps<typeof OriginalMedia> & LightNodeProps,
): ReactElement => (
	<div
		ref={props.lightNodeRef}
		data-collection={props.collection}
		data-local-id={props.localId}
		data-media-id={props.id}
		data-media-type={props.type}
		data-node-type="media"
	>
		<div
			aria-label={props.alt}
			css={styles.media}
			role={props.alt ? 'img' : undefined}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Media dimensions are supplied by the ADF node at runtime to reserve layout space.
			style={getMediaDimensions(props)}
		>
			{props.children}
		</div>
	</div>
);
